// utils/answerValidation.js
import levenshtein from 'js-levenshtein';

/**
 * Normalize a string for comparison:
 * - lowercase
 * - trim whitespace
 * - remove punctuation
 */
export const normalize = (str) => {
  return str
    .trim()
    .toLowerCase()
    .replace(/[.,!?'"-]/g, '') // common punctuation
    .replace(/\s+/g, ' '); // collapse multiple spaces
};

/**
 * Returns true if the normalized string contains only digits.
 * Used to skip fuzzy matching for numeric inputs, preventing e.g. "700" matching "7".
 */
export const isNumeric = (str) => /^\d+$/.test(normalize(str));

/**
 * Exact match comparison
 */
export const isExactMatch = (userInput, correctAnswer) => {
  return normalize(userInput) === normalize(correctAnswer);
};

/**
 * Fuzzy match comparison using Levenshtein distance.
 * Skipped entirely when userInput is numeric to prevent false positives (e.g. "700" matching "7").
 * @param {string} userInput
 * @param {string} correctAnswer
 * @param {number} threshold - maximum distance to allow
 */
export const isFuzzyMatch = (userInput, correctAnswer, threshold = 2) => {
  if (isNumeric(userInput)) return false;
  const distance = levenshtein(normalize(userInput), normalize(correctAnswer));
  return distance <= threshold;
};

/**
 * Token-based matching:
 * - Splits answer into individual words
 * - Checks that all important words are present in user input, order doesn't matter
 */
export const isTokenMatch = (userInput, correctAnswer) => {
  const userTokens = normalize(userInput).split(' ');
  const answerTokens = normalize(correctAnswer).split(' ');

  return answerTokens.every(token => userTokens.includes(token));
};

/**
 * Main validator combining all strategies:
 * 1. Exact match
 * 2. Fuzzy match (minor typos — skipped for numeric inputs)
 * 3. Token match (multi-word order flexibility)
 */
export const validateAnswer = (userInput, correctAnswer) => {
  if (!userInput || !correctAnswer) return false;

  const answers = Array.isArray(correctAnswer) ? correctAnswer : [correctAnswer];

  return answers.some(answer => {
    if (isExactMatch(userInput, answer)) return true;
    if (isFuzzyMatch(userInput, answer, 2)) return true;
    if (isTokenMatch(userInput, answer)) return true;
    return false;
  });
};